'use client';

/**
 * Coverage KPI card shells — four thin cards slicing `response.totals`
 * from the shared useCoverageView composite. Each renders CoverageKpiTile.
 *
 *   Coverage %  ← totals.coverage_pct  (hero, "X / Y used")
 *   Used        ← totals.used
 *   Unused      ← totals.unused
 *   Namespaces  ← by_namespace.length
 *
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { Gauge, CircleCheck, CircleSlash, Boxes } from 'lucide-react';
import { CoverageKpiTile } from './kpi-tile';
import { useCoverageView, KPI_LOADING, fmtInt } from './use-coverage-view';

export function AnalyticsCovKpiCoverageCard() {
  const { data, queryKey } = useCoverageView();
  const L = data?.labels ?? {};
  const t = data?.totals;
  const value = t ? `${t.coverage_pct}${L.unit_pct ?? '%'}` : KPI_LOADING;
  // "1 / 178 used"
  const sub = t ? `${fmtInt(t.used)} / ${fmtInt(t.capabilities)} ${L.cov_kpi_coverage_sub ?? 'used'}` : undefined;
  return (
    <CoverageKpiTile
      label={L.cov_kpi_coverage_label ?? 'Coverage'}
      Icon={Gauge}
      value={value}
      sub={sub}
      accent
      contentKey="analytics_cov_kpi_coverage"
      queryKey={queryKey}
    />
  );
}

export function AnalyticsCovKpiUsedCard() {
  const { data, queryKey } = useCoverageView();
  const L = data?.labels ?? {};
  const t = data?.totals;
  return (
    <CoverageKpiTile
      label={L.cov_kpi_used_label ?? 'Used'}
      Icon={CircleCheck}
      value={t ? fmtInt(t.used) : KPI_LOADING}
      contentKey="analytics_cov_kpi_used"
      queryKey={queryKey}
    />
  );
}

export function AnalyticsCovKpiUnusedCard() {
  const { data, queryKey } = useCoverageView();
  const L = data?.labels ?? {};
  const t = data?.totals;
  return (
    <CoverageKpiTile
      label={L.cov_kpi_unused_label ?? 'Unused'}
      Icon={CircleSlash}
      value={t ? fmtInt(t.unused) : KPI_LOADING}
      contentKey="analytics_cov_kpi_unused"
      queryKey={queryKey}
    />
  );
}

export function AnalyticsCovKpiNamespacesCard() {
  const { data, queryKey } = useCoverageView();
  const L = data?.labels ?? {};
  const ns = data?.by_namespace;
  return (
    <CoverageKpiTile
      label={L.cov_kpi_namespaces_label ?? 'Namespaces'}
      Icon={Boxes}
      value={ns ? fmtInt(ns.length) : KPI_LOADING}
      sub={ns ? (L.cov_kpi_namespaces_sub ?? 'families') : undefined}
      contentKey="analytics_cov_kpi_namespaces"
      queryKey={queryKey}
    />
  );
}
